/* ==============================================================
   Pietà Paperie — semi-custom suite builder
   Rendered by sections/pp-suite-builder.liquid
   Companion files: pp-suite-builder.css, pp-suite-images.js

   PP_CONFIG is emitted inline by the section and carries the
   per-product context: variant ids, money format, and the two
   feature flags below. Everything else is shared across all
   ~30 suite products, which is the point of this structure.
   ============================================================== */
const CFG = Object.assign({
  variants:      {},      /* {simple: id, classic: id, luxury: id}   */
  addonVariants: {},      /* {liner: id, doublePly: id, ...}         */
  moneyFormat:   "${{amount}}",
  cartEnabled:   false,   /* false → show review dialog, add nothing */
  cartUrl:       "/cart",
  shopDomain:    "",      /* stamped into _pp_spec.source.store          */
  currency:      "USD",
  /* Where a guest list spreadsheet is uploaded (the guest-list-upload
     Function URL). Blank: the customer pastes a Google Sheets link. */
  guestListUploadUrl: ""
}, window.PP_CONFIG || {});

/* ============================================================
   PRODUCT DATA — mirrors the live CPB export.
   All 30 suites share this shape; only images/copy differ.
   ============================================================ */
const CURVE = [1.000, 0.951, 0.897, 0.849];   // agreed volume index, applied to add-ons
const BANDS = [
  {min:50,  max:100,      label:"50–100"},
  {min:101, max:150,      label:"101–150"},
  {min:151, max:200,      label:"151–200"},
  {min:201, max:Infinity, label:"201+"}
];

const SUITES = {
  simple:{
    name:"The Simple Suite", rates:[7.48,7.11,6.71,6.35],
    includes:["Invitation card","Outer envelope","Digital proof"],
    linerIncluded:false, detailsCardIncluded:false, rsvp:false,
    press:{letterpress:5.50, foil:8.80}
  },
  classic:{
    name:"The Classic Suite", rates:[14.25,13.53,12.94,12.40],
    includes:["Invitation + RSVP card","Envelope liners included","Addressed outer envelopes","Assembly included"],
    linerIncluded:true, detailsCardIncluded:false, rsvp:true,
    press:{letterpress:6.60, foil:9.90}
  },
  luxury:{
    name:"The Luxury Suite", rates:[25.18,24.17,23.30,22.44],
    includes:["Invitation + RSVP + details card","Envelope liners included","Addressed outer envelopes","Assembly included"],
    linerIncluded:true, detailsCardIncluded:true, rsvp:true,
    press:{letterpress:8.80, foil:12.10}
  }
};

const ADDONS = { liner:4.68, doublePly:2.75, whiteInk:1.67, detailsCard:2.25 };

const INKS = ["Resurrection Gold (as pictured)","Marian Blue","Lavendar's Blue","Dusty Blue","Stella Maris Blue",
  "Navy","Sage","Mystical Rose","Ordinary Thyme","Precious Blood","Vigil","Custom Colour — I'll give you a hex code"];
const INK_SW = ["#B08542","#3D5A8A","#8F9DC4","#9FB4C7","#2E4A6B","#1E2A44","#8A9A7B","#C48B96","#7C8B6B","#7A2233","#4A3B52","#B9B9B9"];
/* Stable ids, parallel to INKS. These are what get PERSISTED on an order;
   the strings in INKS are labels and may be rewritten. Kept in lockstep by
   shared/catalog/catalog.parity.test.mjs. */
const INK_ID = ["resurrection-gold","marian-blue","lavendars-blue","dusty-blue","stella-maris-blue","navy","sage","mystical-rose","ordinary-thyme","precious-blood","vigil","custom"];

/* Bumped when an option is added, removed, or changes meaning. Mirrors
   CATALOG_VERSION in shared/catalog/catalog.ts. */
const CATALOG_VERSION = "2026-10-12";

/* The exact strings written as the "Printing" line item property. The
   legacy order parser matches on these, so they are not free labels. */
const PRESS_LABEL = { digital:"Digital ink", foil:"Gold foil", letterpress:"Letterpress" };

const ENVELOPES = [
  {n:"White",          img:"white",          hex:"#FFFFFF", white:false},
  {n:"Chardonnay",     img:"chardonnay",     hex:"#EFE3C8", white:false},
  {n:"Light Sky Blue", img:"light-sky-blue", hex:"#CBD9E6", white:false},
  {n:"Rain",           img:"rain",           hex:"#B8BFC4", white:false},
  {n:"Powder Pink",    img:"powder-pink",    hex:"#F2D9DA", white:false},
  {n:"Rosa",           img:"rosa",           hex:"#E7BFC4", white:false},
  {n:"Dusty Rose",     img:"dusty-rose",     hex:"#C9A0A0", white:false},
  {n:"Peachy Blush",   img:"peachy-blush",   hex:"#F3D3BC", white:false},
  {n:"Marina",         img:"marina",         hex:"#4C6E8C", white:true},
  {n:"Dark Navy Blue", img:"dark-navy-blue", hex:"#1F2B45", white:true},
  {n:"Ebony Black",    img:"ebony-black",    hex:"#232323", white:true},
  {n:"Merlot",         img:"merlot",         hex:"#5E2230", white:true},
  {n:"Sage",           img:"sage",           hex:"#7E8C72", white:true},
  {n:"Seedling",       img:"seedling",       hex:"#6E7A57", white:true}
];

const LINERS = [
  {n:"Lourdes",                          img:"lourdes"},
  {n:"Song of Mary",                     img:"song-of-mary"},
  {n:"Madonna & Child with Floral Garland", img:"madonna-child-with-floral-garland"},
  {n:"Coronation of Mary",               img:"coronation-of-mary"},
  {n:"Creation of Adam",                 img:"creation-of-adam"},
  {n:"Madonna & Child with Garden Roses", img:"madonna-child-with-garden-roses"},
  {n:"Dove in Flight",                   img:"dove-in-flight"},
  {n:"French Countryside",               img:"french-countryside"},
  {n:"White Garden Roses",               img:"white-garden-roses"},
  {n:"Mary & Joseph's Wedding",          img:"mary-joseph-s-wedding"},
  {n:"Fruitful Love",                    img:"fruitful-love"},
  {n:"Heaven",                           img:"heaven"},
  {n:"David with Roses",                 img:"david-with-roses",        sub:"Matches your ink colour"},
  {n:"Agnus Dei",                        img:"agnus-dei",               sub:"Matches your ink colour"},
  {n:"Auspice Maria",                    img:"auspice-maria",           sub:"Matches your ink colour"},
  {n:"Personalised Monogram",            img:"personalized-monogram",   sub:"Matches your ink colour"},
  {n:"Your church",                      img:"custom-photo-of-your-church", sub:"Custom photo"},
  {n:"A place that matters to you",      img:"custom-photo-of-a-significant-place-to-you", sub:"Custom photo"},
  {n:"You & your fiancé",                img:"custom-photo-of-you-your-fiance", sub:"Custom photo"}
];

const PRAYER = [
  {n:"Feast of the Eucharist",        img:"feast-of-the-eucharist"},
  {n:"The Wedding of Mary and Joseph", img:"the-wedding-of-mary-and-joseph"},
  {n:"Marriage of Mary and Joseph",   img:"marriage-of-mary-and-joseph"},
  {n:"Heavenly Church Wedding",       img:"heavenly-church-wedding"},
  {n:"Wedding of Mary and Joseph",    img:"wedding-of-mary-and-joseph"},
  {n:"Divine Mercy Jesus",            img:"divine-mercy-jesus"},
  {n:"Holy Family",                   img:"holy-family"},
  {n:"Impressionist Flowers",         img:"impressionist-flowers"}
];

/* image lookup — PP_IMAGES ships in pp-suite-images.js */
const pic = (group, slug) =>
  (window.PP_IMAGES && window.PP_IMAGES[group] && window.PP_IMAGES[group][slug]) || "";

/* ============================================================
   PRICING ENGINE
   Monotone clamp: you are never charged more than a larger
   order would cost. This makes every quantity safe to type.
   ============================================================ */
function bandIndex(q){
  for(let i=0;i<BANDS.length;i++) if(q>=BANDS[i].min && q<=BANDS[i].max) return i;
  return q < BANDS[0].min ? 0 : BANDS.length-1;
}
function unitRateAt(i){
  const s = SUITES[state.suite];
  let r = s.rates[i];
  const f = CURVE[i];
  if(state.press === "letterpress") r += s.press.letterpress * f;
  if(state.press === "foil")        r += s.press.foil * f;
  if(state.doublePly)               r += ADDONS.doublePly * f;
  if(state.liner && !s.linerIncluded) r += ADDONS.liner * f;
  if(state.detailsCard)             r += ADDONS.detailsCard * f;
  const env = ENVELOPES.find(e=>e.n===state.envelope);
  if(env && env.white)              r += ADDONS.whiteInk * f;
  return r;
}
/* raw (un-clamped) total at a given quantity */
function rawTotal(q){ return q * unitRateAt(bandIndex(q)); }

/* clamped total + the quantity that produced it */
function priceFor(q){
  let best = rawTotal(q), at = q;
  for(const b of BANDS){
    if(b.min > q){
      const t = rawTotal(b.min);
      if(t < best){ best = t; at = b.min; }
    }
  }
  return {total:best, matchedAt:at, raw:rawTotal(q)};
}

/* ============================================================
   ANSWER RULES — what counts as a real answer, not just a
   non-empty one. Mirrored by shared/catalog/answers.ts for the
   partner path; shared/spec/builder-parity.test.mjs runs both
   over the same inputs.
   ============================================================ */
/* Whole-answer matches only, so a real name that merely contains
   one of these ("Nancy", "Tbdavid") is never refused. */
const PLACEHOLDER_ANSWERS = ["n/a","na","n.a.","none","no","nil","null","-","--","—","–",".","?","tbd","tba","no hosts","no host","not applicable"];
function isPlaceholderAnswer(v){
  const t = String(v||"").trim().toLowerCase().replace(/\s+/g," ");
  return t === "" || PLACEHOLDER_ANSWERS.includes(t);
}
/* The guest list is read from a shared Google Sheet. Anything else —
   a file on the customer's own computer, another host, plain http —
   can't be opened by the studio. */
function isGoogleSheetLink(v){
  let u;
  try { u = new URL(String(v||"").trim()); } catch(e){ return false; }
  return u.protocol === "https:" && u.hostname === "docs.google.com" && u.pathname.startsWith("/spreadsheets/");
}
/* The street addresses ride on "Church" and "Reception", one per line,
   because the pricing cart transform can carry no more properties (its
   input query is at Shopify's complexity cap). Intake splits them back
   out — see packChurch/unpackAddresses in shared/catalog/answers.ts. */
const oneLine = v => String(v||"").replace(/\s+/g," ").trim();
function packChurch(church, street){ const c = oneLine(church), s = oneLine(street); return s ? c + "\n" + s : c; }
function packReception(venue, street, city){
  const v = oneLine(venue), s = oneLine(street), c = oneLine(city);
  return s ? [v, s, c].join("\n") : [v, c].filter(Boolean).join(", ");
}
/* An uploaded guest list's id, as guest-list-upload returns it. */
function isGuestListId(v){ return /^gl_[0-9a-f]{32}$/.test(String(v||"")); }
function uploadsGuestList(){ return !!CFG.guestListUploadUrl; }
/* The reception is printed on the RSVP card and on the details card,
   so it is asked whenever the suite has either. */
function asksReception(s){ return !!(s.rsvp || s.detailsCardIncluded || state.detailsCard); }

/* ============================================================
   STATE
   ============================================================ */
const state = {
  step:0, suite:"classic", qty:100, showErrors:false,
  press:"digital", doublePly:false, liner:false, detailsCard:false,
  ink:INKS[0], customHex:"", envelope:"White", linerArt:LINERS[0].n, prayer:"",
  fields:{}, guestUpload:null
};

const $  = s=>document.querySelector(s);
/* Shopify money formatting — honours the store's format string so
   this keeps working if Markets changes currency or presentment. */
function money(n){
  const parts = (v,dec)=>v.toFixed(dec).replace(/\B(?=(\d{3})+(?!\d))/g,",");
  return CFG.moneyFormat
    .replace(/\{\{\s*amount\s*\}\}/g,               parts(n,2))
    .replace(/\{\{\s*amount_no_decimals\s*\}\}/g,   parts(n,0))
    .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g,
             n.toFixed(2).replace(".","·").replace(/\B(?=(\d{3})+(?!\d))/g,".").replace("·",","))
    .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g,
             n.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g,"."));
}
const el = (tag,cls,html)=>{const e=document.createElement(tag); if(cls)e.className=cls; if(html!=null)e.innerHTML=html; return e;};

/* ============================================================
   STEP RAIL
   ============================================================ */
const STEPS = [
  {n:"Step one",   t:"Your suite"},
  {n:"Step two",   t:"Quantity"},
  {n:"Step three", t:"Make it yours"},
  {n:"Step four",  t:"Your details"}
];
function buildRail(){
  const rail = $("#rail");
  rail.innerHTML = "";
  STEPS.forEach((s,i)=>{
    const b = el("button",null,`<span class="n">${s.n}</span><span class="t">${s.t}</span>`);
    b.type="button";
    b.setAttribute("aria-current", i===state.step ? "true":"false");
    const stepMissing = state.showErrors && missingList().some(m=>m.step===i);
    b.dataset.state = stepMissing ? "todo" : "";
    b.dataset.done = (i < state.step && !stepMissing) ? "true":"false";
    b.onclick = ()=>go(i);
    rail.appendChild(b);
  });
}
function go(i){
  state.step = Math.max(0, Math.min(STEPS.length-1, i));
  document.querySelectorAll(".step").forEach(s=>s.dataset.active = (+s.dataset.step===state.step)?"true":"false");
  $("#back").hidden = state.step===0;
  $("#next").textContent = state.step===STEPS.length-1 ? "Review order →" : "Continue →";
  buildRail();
  window.scrollTo({top:0, behavior:"smooth"});
}

/* ============================================================
   STEP 1 — suites
   ============================================================ */
function buildSuites(){
  const box = $("#suites"); box.innerHTML="";
  Object.entries(SUITES).forEach(([key,s])=>{
    const b = el("button","suite");
    b.type="button";
    b.setAttribute("aria-pressed", state.suite===key ? "true":"false");
    b.innerHTML = `
      <span class="tick">✓</span>
      <h3>${s.name.replace("The ","")}</h3>
      <span class="from">from ${money(s.rates[3])} each</span>
      <ul>${s.includes.map(i=>`<li>${i}</li>`).join("")}</ul>`;
    b.onclick = ()=>{
      state.suite = key;
      if(SUITES[key].linerIncluded) state.liner = false;
      if(SUITES[key].detailsCardIncluded) state.detailsCard = false;
      buildSuites(); buildStyle(); buildDetails(); render();
    };
    box.appendChild(b);
  });
}

/* ============================================================
   STEP 2 — quantity + meter
   ============================================================ */
function buildBands(){
  const box = $("#bands"); box.innerHTML="";
  const active = bandIndex(state.qty);
  BANDS.forEach((b,i)=>{
    const d = el("div","band",`<span class="r">${b.label}</span><span class="p">${money(unitRateAt(i))}</span>`);
    d.dataset.on = i===active ? "true":"false";
    box.appendChild(d);
  });
}
/* ============================================================
   VALIDATION — every required field must be complete
   before the order can be added to cart.
   ============================================================ */
function requiredList(){
  const s = SUITES[state.suite];
  const req = [];
  if(state.ink === INKS[INKS.length-1])
    req.push({id:"hex", label:"Custom ink hex code", step:2, ok:()=>/^#?[0-9a-f]{3,8}$/i.test((state.customHex||"").trim())});

  const v = id => (state.fields[id]||"").trim();
  const f = id => v(id).length>0;
  const add = (id,label)=>req.push({id, label, step:3, ok:()=>f(id)});

  /* A filled-in answer that still doesn't count says why, in the list of
     things still needed — otherwise the field looks done and the button
     refuses for no visible reason. */
  req.push({id:"hosts", step:3, ok:()=>!isPlaceholderAnswer(v("hosts")),
    get label(){ return f("hosts")
      ? "The hosts of the wedding — please write the names to print (your parents', or your own if you are hosting), not N/A or none."
      : "The hosts of the wedding"; }});
  add("bride","Bride's full name");
  add("groom","Groom's full name");
  add("wDate","Wedding date");
  add("wTime","Ceremony time");
  add("church","Name of church");
  add("churchAddr","Church street address");
  add("cityState","City & state of ceremony");
  if(s.rsvp){
    add("rsvpBy","RSVP deadline");
    req.push({id:"rsvpLang", label:"RSVP wording", step:3,
      ok:()=>f("rsvpLang") && state.fields.rsvpLang !== "— Select —"});
  }
  if(asksReception(s)){
    add("venue","Name of reception venue");
    add("venueAddr","Reception street address");
    add("venueCity","City & state of reception");
    req.push({id:"attire", label:"Attire / dress code", step:3,
      ok:()=>f("attire") && state.fields.attire !== "— Select —"});
  }
  add("returnAddr","The hosts' name and return address");
  if(uploadsGuestList()){
    req.push({id:"guestListFile", step:3, ok:()=>isGuestListId(v("guestList")),
      label:"Your guest address list — upload the spreadsheet (CSV or Excel)"});
  } else {
    req.push({id:"guestList", step:3, ok:()=>isGoogleSheetLink(v("guestList")),
      get label(){ return f("guestList")
        ? "Guest address list — this must be a Google Sheets link (https://docs.google.com/spreadsheets/…), not a file on your computer. Upload your list to Google Sheets and paste its share link."
        : "Link to your guest address list"; }});
  }
  req.push({id:"email", label:"Your best email address", step:3,
    ok:()=>/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test((state.fields.email||"").trim())});
  req.push({id:"terms", label:"Agreement to the proof terms", step:3,
    ok:()=>state.fields.terms==="yes"});
  return req;
}
function missingList(){ return requiredList().filter(r=>!r.ok()); }

function paintErrors(){
  document.querySelectorAll(".f.err").forEach(n=>n.classList.remove("err"));
  if(!state.showErrors) return;
  missingList().forEach(r=>{
    const input = document.getElementById(r.id);
    if(input && input.closest(".f")) input.closest(".f").classList.add("err");
  });
}
function renderMissing(){
  const box = $("#missing");
  const miss = missingList();
  if(!state.showErrors || miss.length===0){ box.hidden = true; return; }
  box.hidden = false;
  box.innerHTML = `<h4>${miss.length} thing${miss.length>1?"s":""} still needed</h4>`;
  const ul = el("ul");
  miss.forEach(r=>{
    const li = el("li");
    const b = el("button",null,r.label);
    b.type="button";
    b.onclick = ()=>{
      go(r.step);
      setTimeout(()=>{
        const n = document.getElementById(r.id);
        if(n){ n.scrollIntoView({block:"center", behavior:"smooth"}); n.focus({preventScroll:true}); }
      },220);
    };
    li.appendChild(b); ul.appendChild(li);
  });
  box.appendChild(ul);
}

/* ============================================================
   STEP 3 — style
   ============================================================ */
function optButton({label, sub, amount, on, box, swatch, photo, onClick}){
  const b = el("button", "opt" + (box?" box":""));
  b.type="button";
  b.setAttribute("aria-pressed", on?"true":"false");
  b.innerHTML =
    `<span class="dot"></span>` +
    (photo?`<img class="ph" src="${photo}" alt="" loading="lazy">`
          : swatch?`<span class="sw" style="background:${swatch}"></span>`:``) +
    `<span class="lab">${label}${sub?`<span class="sub">${sub}</span>`:""}</span>` +
    (amount?`<span class="amt">${amount}</span>`:``);
  b.onclick = onClick;
  return b;
}
/* image-tile picker for artwork choices */
function tileGroup(title, hint, items, selected, onPick){
  const fs = el("fieldset");
  fs.appendChild(el("legend",null,title));
  if(hint) fs.appendChild(el("p","hint",hint));
  const g = el("div","tiles");
  items.forEach(it=>{
    const src = pic(it.group, it.img);
    const b = el("button","tile");
    b.type="button";
    b.setAttribute("aria-pressed", selected===it.n ? "true":"false");
    b.innerHTML =
      `<span class="fr">${src?`<img src="${src}" alt="${it.n}" loading="lazy">`:""}<span class="chk">✓</span></span>` +
      `<span class="cap">${it.n}${it.sub?`<small>${it.sub}</small>`:""}</span>`;
    b.onclick = ()=>onPick(it.n);
    g.appendChild(b);
  });
  fs.appendChild(g);
  return fs;
}
function group(title, hint, nodes, cls){
  const fs = el("fieldset");
  fs.appendChild(el("legend",null,title));
  if(hint) fs.appendChild(el("p","hint",hint));
  const g = el("div","opts"+(cls?" "+cls:""));
  nodes.forEach(n=>g.appendChild(n));
  fs.appendChild(g);
  return fs;
}
function buildStyle(){
  const s = SUITES[state.suite];
  const i = bandIndex(state.qty);
  const f = CURVE[i];
  const per = v => v===0 ? "included" : "+"+money(v*f)+" ea";
  const box = $("#style"); box.innerHTML="";

  /* printing */
  box.appendChild(group("Printing method",
    "Letterpress and foil add two weeks to the six-week production time, for eight weeks total before shipping.",
    [
      optButton({label:"Digital ink", amount:"included", on:state.press==="digital",
        onClick:()=>{state.press="digital"; buildStyle(); render();}}),
      optButton({label:"Letterpress", amount:per(s.press.letterpress), on:state.press==="letterpress",
        onClick:()=>{state.press="letterpress"; buildStyle(); render();}}),
      optButton({label:"Gold foil", amount:per(s.press.foil), on:state.press==="foil",
        onClick:()=>{state.press="foil"; buildStyle(); render();}})
    ], "three"));

  /* paper */
  box.appendChild(group("Card thickness", null, [
    optButton({label:"Single-ply", amount:"included", on:!state.doublePly,
      onClick:()=>{state.doublePly=false; buildStyle(); render();}}),
    optButton({label:"Double-ply", sub:"Noticeably heavier, with a folded edge",
      amount:per(ADDONS.doublePly), on:state.doublePly,
      onClick:()=>{state.doublePly=true; buildStyle(); render();}})
  ], "two"));

  /* ink */
  const inkNodes = INKS.map((n,idx)=>optButton({
    label:n, swatch:INK_SW[idx], on:state.ink===n,
    onClick:()=>{state.ink=n; buildStyle(); render();}
  }));
  const inkFs = group("Ink colour", null, inkNodes, "two");
  if(state.ink === INKS[INKS.length-1]){
    const f2 = el("div","f");
    f2.innerHTML = `<label for="hex">Your hex code <span class="req">*</span></label>
      <input id="hex" type="text" placeholder="Example: #FFC0CB" value="${state.customHex}">
      <p class="fh">Not sure how to find one? Message us and we'll match it from a photo.</p>`;
    f2.querySelector("input").oninput = e => state.customHex = e.target.value;
    inkFs.appendChild(f2);
  }
  box.appendChild(inkFs);

  /* envelope */
  box.appendChild(group("Envelope colour",
    "Mid- and dark-toned envelopes are addressed in white ink, which is a separate press run — that's the small surcharge shown.",
    ENVELOPES.map(e=>optButton({
      label:e.n, photo:pic("envelopes",e.img), swatch:e.hex,
      sub:e.white?"White ink addressing":null,
      amount:e.white?per(ADDONS.whiteInk):null,
      on:state.envelope===e.n,
      onClick:()=>{state.envelope=e.n; buildStyle(); render();}
    })), "two"));

  /* liner — add-on only for Simple */
  if(!s.linerIncluded){
    box.appendChild(group("Envelope liners",
      "If you add liners we include outer envelopes as well, so the liners aren't damaged when your guests open the invitation.",
      [ optButton({label:"Add envelope liners", box:true, amount:per(ADDONS.liner), on:state.liner,
          onClick:()=>{state.liner=!state.liner; buildStyle(); buildDetails(); render();}}) ]));
  }

  /* liner artwork */
  if(s.linerIncluded || state.liner){
    box.appendChild(tileGroup("Liner artwork",
      "This is what your guests see when they open the envelope.",
      LINERS.map(l=>({...l, group:"liners"})),
      state.linerArt,
      n=>{ state.linerArt=n; buildStyle(); render(); }));
  }

  /* details card */
  box.appendChild(group("Details card",
    s.detailsCardIncluded
      ? "The Luxury Suite already includes one details card — this adds a second."
      : "A separate insert for accommodation, directions, dress code and your wedding website.",
    [ optButton({
        label: s.detailsCardIncluded ? "Add a second details card" : "Add a details card",
        box:true, amount:per(ADDONS.detailsCard), on:state.detailsCard,
        onClick:()=>{state.detailsCard=!state.detailsCard; buildStyle(); buildDetails(); render();}}) ]));

  /* prayer card */
  box.appendChild(tileGroup("Prayer card artwork",
    "Optional — for your semi-custom prayer cards. Tap again to deselect.",
    PRAYER.map(a=>({...a, group:"prayer-cards"})),
    state.prayer,
    n=>{ state.prayer = state.prayer===n ? "" : n; buildStyle(); render(); }));
}

/* ============================================================
   STEP 4 — details
   ============================================================ */
function field(id,label,ph,req,hint,type){
  const d = el("div","f");
  const t = type||"text";
  const input = t==="textarea"
    ? `<textarea id="${id}" placeholder="${ph||""}">${state.fields[id]||""}</textarea>`
    : `<input id="${id}" type="${t}" placeholder="${ph||""}" value="${state.fields[id]||""}">`;
  d.innerHTML = `<label for="${id}">${label}${req?' <span class="req">*</span>':""}</label>${input}${hint?`<p class="fh">${hint}</p>`:""}`;
  d.querySelector(t==="textarea"?"textarea":"input").oninput = e => state.fields[id]=e.target.value;
  return d;
}
/* ============================================================
   GUEST LIST UPLOAD — the spreadsheet goes to Pieta (see
   amplify/functions/guest-list-upload); only its id rides on the
   cart. Status text is set with textContent: it can carry the
   file's name, which is the customer's own text.
   ============================================================ */
const GUEST_TEMPLATE_CSV = [
  "Name Line 1 (Individuals or Married Couples),Name Line 2 (for Unmarried Couples Living at Same Address),Address Line 1,Address Line 2 (Apartment / Unit),City/State,Zip Code Line,Country,Inner Envelope Name Line 1,Inner Envelope Name Line 2",
  'EXAMPLE: Mr. and Mrs. Arthur Weasley,,123 London Drive,,"Philadelphia, Pennsylvania",12345,,Mr. and Mrs. Weasley,"Charlie, Bill, and Ginny"'
].join("\r\n") + "\r\n";

function guestListStatus(){
  const u = state.guestUpload;
  if(!u) return "CSV or Excel — our template, or any sheet with columns for name, address, city, state and ZIP.";
  if(u.status === "uploading") return "Uploading " + u.name + "…";
  if(u.status === "error") return u.message;
  return u.count + " guest" + (u.count === 1 ? "" : "s") + " found in " + u.name +
    (u.warnings ? " — " + u.warnings + " row" + (u.warnings === 1 ? "" : "s") + " to check; we'll confirm " + (u.warnings === 1 ? "it" : "them") + " with you." : ".");
}

function guestListField(){
  const d = el("div","f");
  d.innerHTML = '<label for="guestListFile">Your guest address list <span class="req">*</span></label>' +
    '<input id="guestListFile" type="file" accept=".csv,.xlsx,.xls">' +
    '<p class="fh"></p>' +
    '<p class="fh"><a download="Pieta guest list template.csv">Download our template</a></p>';
  d.querySelector(".fh").textContent = guestListStatus();
  d.querySelector("a[download]").href = "data:text/csv;charset=utf-8," + encodeURIComponent(GUEST_TEMPLATE_CSV);
  d.querySelector("input").onchange = e => { const file = e.target.files && e.target.files[0]; if(file) uploadGuestList(file); };
  return d;
}

/* Only the field's status line changes while a file uploads; the rest of the form stays put. */
function refreshGuestListStatus(){
  const input = document.getElementById("guestListFile");
  const line = input && input.closest(".f") && input.closest(".f").querySelector(".fh");
  if(line) line.textContent = guestListStatus();
}

let guestUploadSeq = 0;
async function uploadGuestList(file){
  /* Only the last file picked counts, whichever upload finishes first. */
  const seq = ++guestUploadSeq;
  state.fields.guestList = "";
  state.guestUpload = { status:"uploading", name:file.name };
  refreshGuestListStatus(); render();
  try{
    const res = await fetch(CFG.guestListUploadUrl, {
      method:"POST",
      /* Always bytes: a text/csv body would reach the server as already-decoded text, and
         Excel's own CSV is Windows-1252, which the server decodes itself. */
      headers:{ "x-file-name": file.name, "content-type": "application/octet-stream" },
      body:file
    });
    const data = await res.json().catch(()=>({}));
    if(seq !== guestUploadSeq) return;
    if(!res.ok || !data || !isGuestListId(data.guestListId)){
      state.guestUpload = { status:"error", name:file.name,
        message:(data && data.error && data.error.message) || "The guest list could not be uploaded. Please try again." };
    } else {
      state.fields.guestList = data.guestListId;
      state.guestUpload = { status:"done", name:file.name, count:data.count, warnings:(data.warnings||[]).length };
    }
  } catch(e){
    if(seq !== guestUploadSeq) return;
    state.guestUpload = { status:"error", name:file.name, message:"The guest list could not be uploaded. Check your connection and try again." };
  }
  refreshGuestListStatus(); render();
}

function selectField(id,label,opts,req){
  const d = el("div","f");
  d.innerHTML = `<label for="${id}">${label}${req?' <span class="req">*</span>':""}</label>
    <select id="${id}">${["— Select —"].concat(opts).map(o=>`<option${state.fields[id]===o?" selected":""}>${o}</option>`).join("")}</select>`;
  d.querySelector("select").onchange = e => state.fields[id]=e.target.value;
  return d;
}
function sect(title,hint,nodes){
  const fs = el("fieldset");
  fs.appendChild(el("legend",null,title));
  if(hint) fs.appendChild(el("p","hint",hint));
  nodes.forEach(n=>fs.appendChild(n));
  return fs;
}
function buildDetails(){
  const s = SUITES[state.suite];
  const box = $("#details"); box.innerHTML="";

  box.appendChild(sect("Who is hosting", "Traditionally the bride's parents, but it can be anyone — including the two of you.", [
    field("hosts","The hosts of the wedding","Example: Mr. & Mrs. William Edward Smith",true,
      "This goes at the very top of your invitation, so please write names — your parents', or your own if you are hosting."),
    field("hostRel","Your relationship to the hosts","Example: The bride's parents",false,
      "So we can word the invitation correctly.")
  ]));

  box.appendChild(sect("The couple", null, [
    (()=>{const p=el("div","pair");
      p.appendChild(field("bride","Bride's full name","Example: Chloe Marie Smith",true));
      p.appendChild(field("groom","Groom's full name","Example: Joshua Fulton Jones",true));
      return p;})()
  ]));

  box.appendChild(sect("The ceremony", null, [
    (()=>{const p=el("div","pair");
      p.appendChild(field("wDate","Wedding date",null,true,null,"date"));
      p.appendChild(field("wTime","Ceremony time",null,true,null,"time"));
      return p;})(),
    field("church","Name of church","Example: St. Patrick's Catholic Church",true),
    field("churchAddr","Church street address","Example: 123 Main Street",true),
    field("cityState","City & state of ceremony","Example: Winchester, Virginia",true)
  ]));

  if(s.rsvp){
    box.appendChild(sect("RSVP card", null, [
      field("rsvpBy","RSVP deadline",null,true,
        "Ideally three to four weeks before the wedding — earlier if you need your final headcount sooner.","date"),
      selectField("rsvpLang","RSVP wording",
        ["Graciously accepts / Respectfully declines","Joyfully accepts / Regretfully declines"],true),
      field("entree","Entrée selection, if you're offering one","",false,"This goes on the back of your RSVP card.","textarea")
    ]));
  }

  if(asksReception(s)){
    box.appendChild(sect("The reception", null, [
      field("venue","Name of reception venue","Example: The Villa Maria Inn",true),
      field("venueAddr","Reception street address","Example: 456 Harbor Road",true),
      field("venueCity","City & state of reception","Example: Ocean City, Maryland",true),
      field("cocktail","Cocktail hour start time",null,false,null,"time"),
      selectField("attire","Attire / dress code",
        ["White tie (super formal)","Black tie (very formal)","Black tie optional (formal)",
         "Cocktail (a step above semi-formal)","Semi-formal (dressy casual)","Please don't add attire"],true)
    ]));
  }

  box.appendChild(sect("Your wedding website", null, [
    field("website","Wedding website (and guest password, if there is one)","Example: theknot.com/jack-and-jill",false)
  ]));

  if(s.detailsCardIncluded || state.detailsCard){
    box.appendChild(sect("Details card", null, [
      selectField("dcWeb","Add your website to the details card?",
        ["Yes — the URL","Yes — a QR code","Yes — both the URL and a QR code","No thank you"],false),
      field("dcInfo","What should the details card say?",
        "Example: Accommodations — a hotel block has been reserved at…",false,null,"textarea")
    ]));
  }

  box.appendChild(sect("Addressing", "We hand-address your envelopes from the list you give us.", [
    field("returnAddr","The hosts' name and return address",
      "Example: Miss Sophia Kennedy, 123 Seashell Drive, Ocean City, Maryland 12345",true,
      "This goes on the back flap of your envelopes."),
    uploadsGuestList() ? guestListField() :
    field("guestList","Link to your guest address list","https://docs.google.com/spreadsheets/…",true,
      'A Google Sheets link — a file on your computer (Numbers, Excel) can\'t be opened by our studio. Please follow <a href="#">our template</a> and set sharing so anyone with the link can view.',"url"),
    field("extra","Anything else we should know?","",false,"Tell us which card it's for.","textarea")
  ]));

  box.appendChild(sect("Finally", null, [
    field("email","Your best email address","Example: myemail@gmail.com",true,
      "This is where we send your digital proof for approval, so use the one you check most.","email"),
    (()=>{
      const d = el("div","opts");
      d.appendChild(optButton({
        label:"I understand that a digital proof will be emailed for my approval before printing, and that one round of revisions is included.",
        box:true, on:state.fields.terms==="yes",
        onClick:()=>{state.fields.terms = state.fields.terms==="yes"?"":"yes"; buildDetails(); render();}
      }));
      return d;
    })()
  ]));
}

/* ============================================================
   SUMMARY + RENDER
   ============================================================ */
function render(){
  const s = SUITES[state.suite];
  const i = bandIndex(state.qty);
  const f = CURVE[i];
  const p = priceFor(state.qty);
  const effUnit = p.total / state.qty;

  $("#unitRate").innerHTML = money(unitRateAt(i));
  buildBands();

  const lines = $("#lines"); lines.innerHTML="";
  const add = (k,sub,v)=>lines.appendChild(el("div","line",
    `<span class="k">${k}${sub?`<small>${sub}</small>`:""}</span><span class="v">${v}</span>`));

  add(s.name.replace("The ",""), `${state.qty} × ${money(s.rates[i])}`, money(s.rates[i]*state.qty));
  if(state.press==="letterpress") add("Letterpress", `${state.qty} × ${money(s.press.letterpress*f)}`, money(s.press.letterpress*f*state.qty));
  if(state.press==="foil")        add("Gold foil",  `${state.qty} × ${money(s.press.foil*f)}`,        money(s.press.foil*f*state.qty));
  if(state.doublePly)             add("Double-ply", `${state.qty} × ${money(ADDONS.doublePly*f)}`,    money(ADDONS.doublePly*f*state.qty));
  if(state.liner && !s.linerIncluded) add("Envelope liners", `${state.qty} × ${money(ADDONS.liner*f)}`, money(ADDONS.liner*f*state.qty));
  if(state.detailsCard)           add(s.detailsCardIncluded?"Second details card":"Details card", `${state.qty} × ${money(ADDONS.detailsCard*f)}`, money(ADDONS.detailsCard*f*state.qty));
  const env = ENVELOPES.find(e=>e.n===state.envelope);
  if(env && env.white)            add("White ink addressing", `${state.qty} × ${money(ADDONS.whiteInk*f)}`, money(ADDONS.whiteInk*f*state.qty));

  const savedRow = $("#savedRow");
  const saving = p.raw - p.total;
  if(saving > 0.005){
    savedRow.hidden = false;
    $("#savedAmt").innerHTML = "−" + money(saving);
  } else savedRow.hidden = true;

  $("#total").innerHTML = money(p.total);
  $("#totalSub").innerHTML = `${state.qty} × ${money(effUnit)} each`;

  const miss = missingList();
  const cta = $("#addCart");
  cta.textContent = miss.length ? `Add to cart — ${miss.length} field${miss.length>1?"s":""} left` : "Add to cart";
  cta.disabled = false;             // always clickable, so it can explain what's missing
  cta.dataset.ready = miss.length ? "false" : "true";
  paintErrors(); renderMissing(); buildRail();
}

/* ============================================================
   WIRING
   ============================================================ */
const qtyInput = $("#qty");
function setQty(v){
  let q = parseInt(v,10);
  if(isNaN(q)) q = 50;
  q = Math.max(50, Math.min(5000, q));
  state.qty = q;
  qtyInput.value = q;
  buildStyle(); render();
}
qtyInput.addEventListener("input", e=>{
  const q = parseInt(e.target.value,10);
  if(!isNaN(q) && q>=50){ state.qty=q; buildStyle(); render(); }
});
qtyInput.addEventListener("blur", e=>setQty(e.target.value));
$("#qminus").onclick = ()=>setQty(state.qty-5);
$("#qplus").onclick  = ()=>setQty(state.qty+5);

$("#next").onclick = ()=>{
  if(state.step === STEPS.length-1){ attemptSubmit(); return; }
  go(state.step+1);
};
$("#back").onclick = ()=>go(state.step-1);
$("#peek").onclick = ()=>{
  const s=$("#sum"); s.classList.toggle("open");
  $("#peek").textContent = s.classList.contains("open") ? "Hide breakdown" : "Show breakdown";
};
function attemptSubmit(){
  state.showErrors = true;
  render();
  const miss = missingList();
  if(miss.length){
    const first = miss[0];
    $("#sum").classList.add("open");
    go(first.step);
    setTimeout(()=>{
      const n = document.getElementById(first.id);
      if(n){ n.scrollIntoView({block:"center", behavior:"smooth"}); n.focus({preventScroll:true}); }
    },220);
    return;
  }
  const p = priceFor(state.qty);
  const s = SUITES[state.suite];
  const rows = [
    ["Suite", s.name.replace("The ","")],
    ["Quantity", state.qty + " invitations"],
    ["Printing", state.press==="digital"?"Digital ink":state.press==="foil"?"Gold foil":"Letterpress"],
    ["Paper", state.doublePly ? "Double-ply" : "Single-ply"],
    ["Ink", state.ink.replace(" (as pictured)","")],
    ["Envelope", state.envelope]
  ];
  if(s.linerIncluded || state.liner) rows.push(["Liner", state.linerArt]);
  if(state.prayer) rows.push(["Prayer card", state.prayer]);
  rows.push(["Total", money(p.total)]);
  if(CFG.cartEnabled){ addToCart(); return; }

  $("#modalList").innerHTML = rows
    .map(([k,v])=>`<dt>${k}</dt><dd>${v}</dd>`).join("");
  $("#modal").hidden = false;
  $("#modalClose").focus();
}

/* ============================================================
   CART
   The configuration travels as line item properties so the
   studio sees the full spec on the order, and so the pricing
   Function can read what it needs at checkout.

   Line shape is deliberately isolated here: whichever pricing
   mechanism we settle on, this is the only function that changes.
   ============================================================ */
/* ------------------------------------------------------------
   THE SPEC

   One versioned payload of stable identifiers describing the whole
   configuration. It is the ONLY authored representation of the design:
   the human-readable properties below are derived from it, never written
   alongside it, because two hand-written representations of the same
   configuration drift and nobody notices.

   Contract and field meanings: shared/spec/suite-spec.ts, and
   docs/partner-suite-integration.md section 4. This function and
   buildSuiteSpec() there must produce identical output; the theme cannot
   import TypeScript, so agreement is checked by
   shared/catalog/catalog.parity.test.mjs rather than assumed.

   Nothing here is free text. Names, dates, the guest list and the wording
   stay as ordinary visible properties — Shopify caps property values and
   this payload has to stay small and predictable.
   ------------------------------------------------------------ */
function suiteSpec(){
  const s   = SUITES[state.suite];
  const p   = priceFor(state.qty);
  const ix  = INKS.indexOf(state.ink);
  const env = ENVELOPES.find(e=>e.n===state.envelope) || {};
  const isCustomInk = ix === INKS.length-1;
  const customHex = ("#" + (state.customHex||"").trim().replace(/^#/,"")).toUpperCase();
  const linerOn = !!(s.linerIncluded || state.liner);
  const liner = linerOn ? LINERS.find(l=>l.n===state.linerArt) : null;
  const pray  = state.prayer ? PRAYER.find(x=>x.n===state.prayer) : null;

  /* Refuse to build a spec we cannot resolve, rather than emitting an empty id.
     An empty id fails validation on the way back in, the line silently falls
     back to labels that were themselves derived from the broken spec, and it
     prices as if the envelope were plain. Failing here is loud and local. */
  if(!env.img || !INK_ID[ix] || (linerOn && !liner) || (state.prayer && !pray)){
    throw new Error("pp-suite-builder: cannot resolve a catalog id for the current selection");
  }

  return {
    version: 1,
    source: { store: CFG.shopDomain, channel: "pieta-web" },
    design: {
      suite:      state.suite,
      press:      state.press,
      cardStock:  state.doublePly ? "double-ply" : "single-ply",
      envelope:   { id: env.img, hex: env.hex, whiteInk: !!env.white },
      ink:        { id: INK_ID[ix], hex: isCustomInk && state.customHex ? customHex : INK_SW[ix] },
      linerArtwork: liner ? liner.img : null,
      prayerCard:   pray  ? pray.img  : null,
      detailsCard:  !!(s.detailsCardIncluded || state.detailsCard),
      rsvpCard:     !!s.rsvp
    },
    pricing: {
      quantity:          state.qty,
      currency:          CFG.currency,
      quotedUnitAmount:  unitRateAt(bandIndex(state.qty)).toFixed(2),
      quotedTotalAmount: p.total.toFixed(2),
      band:              BANDS[bandIndex(state.qty)].label,
      catalogVersion:    CATALOG_VERSION
    }
  };
}

/* The visible properties, derived from the spec. Mirrors
   displayPropertiesFromSpec() in shared/spec/suite-spec.ts. */
function labelsFromSpec(spec){
  const suite = SUITES[spec.design.suite];
  const env   = ENVELOPES.find(e=>e.img===spec.design.envelope.id);
  const ix    = INK_ID.indexOf(spec.design.ink.id);
  const liner = LINERS.find(l=>l.img===spec.design.linerArtwork);
  const pray  = PRAYER.find(x=>x.img===spec.design.prayerCard);

  const props = {
    "Suite":           suite ? suite.name : spec.design.suite,
    "Printing":        PRESS_LABEL[spec.design.press] || spec.design.press,
    "Card thickness":  spec.design.cardStock === "double-ply" ? "Double-ply" : "Single-ply",
    "Ink colour":      spec.design.ink.id === "custom"
                         ? "Custom — " + spec.design.ink.hex
                         : (ix >= 0 ? INKS[ix] : spec.design.ink.id),
    "Envelope colour": env ? env.n : spec.design.envelope.id
  };
  if(liner) props["Liner artwork"] = liner.n;
  if(pray)  props["Prayer card"]   = pray.n;
  return props;
}

function cartProperties(){
  const s = SUITES[state.suite];
  const f = state.fields;
  const spec = suiteSpec();
  const props = Object.assign(labelsFromSpec(spec), {
    /* Not derived from the spec: a shopper can change quantity in the cart
       afterwards, and the studio must read the line they actually bought. */
    "Quantity":         String(state.qty),
    "Hosts":            f.hosts || "",
    "Bride":            f.bride || "",
    "Groom":            f.groom || "",
    "Wedding date":     [f.wDate, f.wTime].filter(Boolean).join(" "),
    "Church":           packChurch(f.church, f.churchAddr),
    "Ceremony city":    f.cityState || "",
    "Return address":   f.returnAddr || "",
    /* An upload's id (read by intake) with its count, or the Sheets link. */
    "Guest list":       isGuestListId(f.guestList)
                          ? "Uploaded: " + ((state.guestUpload && state.guestUpload.count) || 0) + " guests (" + f.guestList + ")"
                          : (f.guestList || ""),
    "Proof email":      f.email || "",
    /* read by the pricing Function — do not rename without updating it */
    "_pp_unit_rate":    spec.pricing.quotedUnitAmount,
    "_pp_total":        spec.pricing.quotedTotalAmount,
    "_pp_tier":         spec.pricing.band,
    /* The configuration itself. Preferred over every label above by the
       pricing Function, the order workspace and any reprint. */
    "_pp_spec":         JSON.stringify(spec)
  });
  /* "Liner artwork" and "Prayer card" come from labelsFromSpec(). */
  if(s.rsvp){
    props["RSVP by"]        = f.rsvpBy || "";
    props["RSVP wording"]   = f.rsvpLang || "";
    props["Entrées"]        = f.entree || "";
  }
  if(asksReception(s)){
    props["Reception"]      = packReception(f.venue, f.venueAddr, f.venueCity);
    props["Cocktail hour"]  = f.cocktail || "";
    props["Attire"]         = f.attire || "";
  }
  if(s.detailsCardIncluded || state.detailsCard){
    props["Details card"]      = f.dcWeb || "Yes";
    props["Details card copy"] = f.dcInfo || "";
  }
  if(f.website) props["Wedding website"] = f.website;
  if(f.extra)   props["Additional notes"] = f.extra;
  Object.keys(props).forEach(k=>{ if(props[k]==="") delete props[k]; });
  return props;
}

function cartLines(){
  /* One line only. The Cart Transform function reads this line's own
     properties (Suite, Printing, Card thickness, Envelope colour,
     Liner artwork, Details card) and expands it into a single
     component priced at the correct blended rate for the full
     configuration -- see cart_transform_run.ts. Add-ons are never
     separate cart lines or separate variants; they exist only as
     these text properties, which is also what the order-pricing
     admin tool and the quote calculator read them back from. */
  return [{
    id: CFG.variants[state.suite],
    quantity: state.qty,
    properties: cartProperties()
  }];
}

async function addToCart(){
  const cta = $("#addCart");
  const original = cta.textContent;
  cta.disabled = true; cta.textContent = "Adding…";
  try{
    const res = await fetch("/cart/add.js",{
      method:"POST",
      headers:{"Content-Type":"application/json", "Accept":"application/json"},
      body: JSON.stringify({ items: cartLines() })
    });
    if(!res.ok){
      const err = await res.json().catch(()=>({}));
      throw new Error(err.description || err.message || ("Cart returned " + res.status));
    }
    window.location.href = CFG.cartUrl;
  }catch(e){
    cta.disabled = false; cta.textContent = original;
    const box = $("#missing");
    box.hidden = false;
    box.innerHTML = `<h4>Couldn't add to cart</h4><ul><li>${e.message}</li></ul>`;
  }
}
$("#modalClose").onclick = ()=>{ $("#modal").hidden = true; $("#addCart").focus(); };
$("#modal").onclick = e=>{ if(e.target === $("#modal")) $("#modal").hidden = true; };
document.addEventListener("keydown", e=>{
  if(e.key === "Escape" && !$("#modal").hidden) $("#modal").hidden = true;
});
$("#addCart").onclick = attemptSubmit;

/* The section renders the product's own featured image server-side, which is
   what Google and the social crawlers read. Only fall back to the catalogue
   image if the product has none — never overwrite what Liquid rendered. */
(function(){
  const hero = $("#hero");
  if(!hero) return;
  if(hero.getAttribute("src")) return;
  const fallback = pic("suite-photos","front");
  if(fallback) hero.src = fallback; else hero.remove();
})();

buildRail(); buildSuites(); buildStyle(); buildDetails(); render();
